import React, { useState, useEffect } from 'react';
import { Avatar, Button, TextArea, TextBox, Table } from '@ds/ui';
import { BannerInfo } from '../../types/BannerInfo';
import './RestoreKeys.css';
import { apiFetch, handleApiError } from '../../utils/apiClient';

interface RestoreKeysProps {
  onBannerUpdate: (bannerInfo: BannerInfo | null) => void;
}

interface RestoreFormData {
  datacenterId: string;
  hostname: string;
  port: string;
  partitionId: string;
  blobId: string;
  blueKey: string;
  decryptedRedKey: string;
}

enum RestoreStep
{
    Unspecified = 0,
    GetBlob = 1,
    EnterBlueKey = 2,
    EnterRedKey = 3,
    Import = 4,
    Cancel = 5
}


export default function RestoreKeys({ onBannerUpdate }: RestoreKeysProps) {
  const [formData, setFormData] = useState<RestoreFormData>({
    datacenterId: '',
    hostname: 'localhost',
    port: '8002',
    partitionId: '',
    blobId: '',
    blueKey: '',
    decryptedRedKey: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRestore = async (step: RestoreStep) => {
    try {
      const response = await apiFetch('Admin/Backup/Restore', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          step,
          ...formData
        })
      });

      if (!response.ok) {
        await handleApiError(response);
      }
      var message = '';
      switch (step) {
        case RestoreStep.Cancel:
          message = 'Restore cancelled';
          break;
        case RestoreStep.GetBlob:
          message = 'Successfully retrieved backup blob';
          break;
        case RestoreStep.EnterBlueKey:
          message = 'Successfully entered blue key';
          break;
        case RestoreStep.EnterRedKey:
          message = 'Successfully entered decrypted red key';
          break;
        case RestoreStep.Import:
          message = 'Successfully imported keys';
          break;
      }
      
      onBannerUpdate({
        type: 'success',
        message: message
      });
    } catch (error) {
      onBannerUpdate({
        type: 'danger',
        message: error instanceof Error ? error.message : 'Failed to get the backup blob',
        retryAction: () => handleRestore(step)  
      });
    }
  };


  return (
    <div>
      <div className="restore-keys">
        <section className="featured">
          <div className="content-wrapper">
            <hgroup className="title">
              <h1>Restore Keys From Backup</h1>
            </hgroup>
          </div>
        </section>

        <Table>
          <Table.Body>
            
            <Table.Row>
              <Table.Cell _style={{ textAlign: 'left' }} colSpan={2}>
                <div style={{ textAlign: 'left', paddingLeft: '100px' }}>
                  <p>Select a row from the dbo.ExportKey table in the DocuSignSecurity database.</p>
                  <p>Enter the datacenterId, hostname and port for the Discovery service.</p>
                  <p>Enter the Partition Id and Blob Id from the selected row in the dbo.ExportKey table.</p>
                  <p>Enter the Blue Key from the selected row in the dbo.ExportKey table.</p>
                  <p>Decrypt the red key by running Components\Quartz.8616\bin\SecurityApplianceRestoreTool.ps1.</p>
                  <p>Enter the decrypted Red Key.</p>
                </div>
                <br />
              </Table.Cell>
              </Table.Row>
          </Table.Body>
        </Table>
      </div>

      <div className="restore-keys">
        <Table>
          <Table.Body>
            <Table.Cell colSpan={2}>
              <ul style={{ paddingTop: '20px' }}>
                <ol className="restore-keys">
                  <li className="step-1">
                    <Table>
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell _style={{ textAlign: 'left' }}>Discovery DataCenterId:</Table.Cell>
                          <Table.Cell>
                            <TextBox
                              label=""
                              name="datacenterId"
                              value={formData.datacenterId}
                              onChange={handleInputChange}
                            />
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell _style={{ textAlign: 'left' }}>Discovery hostname:</Table.Cell>
                          <Table.Cell>
                            <TextBox
                              label=""
                              name="hostname"
                              value={formData.hostname}
                              onChange={handleInputChange}
                              placeholder="localhost"
                            />
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell _style={{ textAlign: 'left' }}>Discovery port:</Table.Cell>
                          <Table.Cell>
                            <TextBox
                              label=""
                              name="port"
                              value={formData.port}
                              onChange={handleInputChange}
                            />
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell _style={{ textAlign: 'left' }}>Partition ID:</Table.Cell>
                          <Table.Cell>
                            <TextBox
                              label=""
                              name="partitionId"
                              value={formData.partitionId}
                              onChange={handleInputChange}
                            />
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell _style={{ textAlign: 'left' }}>Blob ID:</Table.Cell>
                          <Table.Cell>
                            <TextBox
                              label=""
                              name="blobId"
                              value={formData.blobId}
                              onChange={handleInputChange}
                            />
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell _style={{ textAlign: 'left', paddingTop: '1.875rem' }} colSpan={2}>
                            <Button
                              text="Get Backup Blob"
                              onClick={() => handleRestore(RestoreStep.GetBlob)}
                              kind="secondary"
                            />
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table>
                  </li>

                  <li className="step-2">
                    <Table>
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell _style={{ textAlign: 'left', paddingTop: '30px' }}>
                            <TextArea
                              label=""
                              name="blueKey"
                              value={formData.blueKey}
                              onChange={handleInputChange}
                              rows={7}
                            />
                            <br/>
                            <Button 
                              text="Enter Blue Key" 
                              kind="secondary" 
                              onClick={() => handleRestore(RestoreStep.EnterBlueKey)} 
                            />
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table>
                  </li>

                  <li className="step-3">
                    <Table>
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell _style={{ textAlign: 'left', paddingTop: '30px' }}>
                            <TextArea
                              label=""
                              name="decryptedRedKey"
                              value={formData.decryptedRedKey}
                              onChange={handleInputChange}
                              rows={7}
                            />
                            <br/>
                            <Button 
                              text="Enter Decrypted Red Key" 
                              kind="secondary" 
                              onClick={() => handleRestore(RestoreStep.EnterRedKey)} 
                            />
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table>
                  </li>

                  <li className="step-4">
                    <Table>
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell _style={{ textAlign: 'left', paddingTop: '30px' }}>
                            <Button
                              text="Import Keys"
                              onClick={() => handleRestore(RestoreStep.Import)}
                              kind="primary"
                            />
                            <span style={{ margin: '0 1.5rem' }}>or</span>
                            <Button
                              text="Cancel"
                              onClick={() => handleRestore(RestoreStep.Cancel)}
                              kind="secondary"
                            />
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table>
                  </li>
                </ol>
              </ul>
            </Table.Cell>
          </Table.Body>
        </Table>
      </div>
    </div>
  );
}